import React, { useState, useEffect, useContext } from 'react';
import { getToken } from '../../utils/tokenUtils';
import { DollarSign, TrendingUp, FileText, Calendar, Eye, Download, ArrowRight } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { FaRupeeSign } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { getRoleBasedRoute } from "../../utils/routeUtils";
import { AuthContext } from "../../contexts/AuthContext";
import StatusBadge from '../../components/UI/StatusBadge';

const EmployeeDashboard = () => {
  const [latestPayroll, setLatestPayroll] = useState(null);
  const [salaryStructure, setSalaryStructure] = useState(null);
  const [recentPayrolls, setRecentPayrolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const token = getToken();
  const navigate = useNavigate();
  const { roles } = useContext(AuthContext);

  const getRoleBasedRouteLocal = (route) => {
    return getRoleBasedRoute(route, roles);
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch recent payrolls
      const payrollResponse = await fetch('https://hrmspayroll.prepstripe.com/api/payroll/my-payrolls?per_page=6', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const payrollData = await payrollResponse.json();
      const payrolls = payrollData.data.data || [];
      setRecentPayrolls(payrolls);
      setLatestPayroll(payrolls[0] || null);

      // Fetch salary structure
      const employeeId = 'EMP001'; // Mock - get from auth context in real app
      const structureResponse = await fetch(`https://hrmspayroll.prepstripe.com/api/payroll/salary-structures/${employeeId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const structureData = await structureResponse.json();
      setSalaryStructure(structureData);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const downloadPayslip = async (recordId) => {
    try {
      const response = await fetch(`https://hrmspayroll.prepstripe.com/api/payroll/payslip/${recordId}/download`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `payslip_${recordId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading payslip:', error);
    }
  };

  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  // Prepare chart data
  const salaryTrendData = recentPayrolls.slice(-6).reverse().map(record => ({
    month: months[record.month - 1],
    net: record.net_salary,
    gross: record.gross_salary
  }));

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto animate-pulse">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="bg-white rounded-lg h-32"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3">
      <div className="flex flex-col gap-2">
        {/* Header */}
        <div className='mb-6'>
          <h1 className="text-3xl font-bold text-gray-900">My Payroll Dashboard</h1>
          <p className="text-gray-600 mt-2">Overview of your salary and payroll information</p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 ">
          {/* Latest Net Salary */}
          <div className="bg-white rounded-md shadow-md p-3">
            <div className="flex items-center space-x-3">
              <FaRupeeSign className="h-5 w-5 text-green-500" />
              <div className="flex flex-col gap-1">
                <p className="text-sm text-gray-600 leading-tight">Latest Net Salary</p>
                <p className="text-base font-semibold text-gray-900">
                  ₹ {latestPayroll?.net_salary?.toLocaleString() || '0'}
                </p>
              </div>
            </div>
          </div>

          {/* Annual CTC */}
          <div className="bg-white rounded-md shadow-md p-3">
            <div className="flex items-center space-x-3">
              <TrendingUp className="h-5 w-5 text-blue-500" />
              <div className="flex flex-col gap-1">
                <p className="text-sm text-gray-600 leading-tight">Annual CTC</p>
                <p className="text-base font-semibold text-gray-900">
                  ₹ {salaryStructure?.ctc?.toLocaleString() || '0'}
                </p>
              </div>
            </div>
          </div>

          {/* Available Payslips */}
          <div className="bg-white rounded-md shadow-md p-3">
            <div className="flex items-center space-x-3">
              <FileText className="h-5 w-5 text-purple-500" />
              <div className="flex flex-col gap-1">
                <p className="text-sm text-gray-600 leading-tight">Available Payslips</p>
                <p className="text-base font-semibold text-gray-900">{recentPayrolls.length}</p>
              </div>
            </div>
          </div>

          {/* Last Updated */}
          <div className="bg-white rounded-md shadow-md p-3">
            <div className="flex items-center space-x-3">
              <Calendar className="h-5 w-5 text-orange-500" />
              <div className="flex flex-col gap-1">
                <p className="text-sm text-gray-600 leading-tight">Last Updated</p>
                <p className="text-sm font-semibold text-gray-900">
                  {latestPayroll
                    ? new Date(latestPayroll.created_at).toLocaleDateString()
                    : 'N/A'}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="">
          {/* Salary Trend */}
          {/* <div className="lg:col-span-2 bg-white rounded-md shadow-md p-3">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Salary Trend (Last 6 Months)</h3>
            {salaryTrendData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={salaryTrendData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis tickFormatter={(value) => `₹${(value / 1000).toFixed(0)}K`} />
                  <Tooltip formatter={(value) => [`₹${value.toLocaleString()}`, 'Amount']} />
                  <Line type="monotone" dataKey="gross" stroke="#3b82f6" strokeWidth={2} name="Gross Salary" />
                  <Line type="monotone" dataKey="net" stroke="#10b981" strokeWidth={2} name="Net Salary" />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-64 text-gray-500">
                <div className="text-center">
                  <TrendingUp className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No salary trend data available</p>
                </div>
              </div>
            )}
          </div> */}

          {/* Quick Actions */}
          <div className="bg-white rounded-md shadow-md p-3">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
            <div className=" grid grid-cols-3  gap-6 space-y-3">
              <button
                onClick={() => latestPayroll && downloadPayslip(latestPayroll.id)}
                disabled={!latestPayroll}
                className="w-full flex items-center justify-between text-black shadow-md p-2 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="flex items-center">
                  <Download className="h-5 w-5 mr-2 ml-1" />
                  <span className="font-medium text-sm">Download Payslip</span>
                </div>
                <ArrowRight className="h-4 w-4" />
              </button>
              
              <button
                onClick={() => navigate(getRoleBasedRouteLocal('/admin/my-payroll/payslips'))}
                className="w-full flex items-center justify-between p-2 text-black shadow-md rounded-lg hover:bg-gray-50"
              >
                <div className="flex items-center">
                  <Eye className="h-5 w-5 mr-2 ml-1" />
                  <span className="font-medium text-sm">View All Payslips</span>
                </div>
                <ArrowRight className="h-4 w-4" />
              </button>
              
              <button
                onClick={() => navigate(getRoleBasedRouteLocal('/admin/my-payroll/salary-structure'))}
                className="w-full flex items-center justify-between p-2 text-black shadow-md rounded-lg hover:bg-gray-50"
              >
                <div className="flex items-center">
                  <FileText className="h-5 w-5 mr-2 ml-1" />
                  <span className="font-medium text-sm">View Salary Structure</span>
                </div>
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Recent Payrolls */}
        <div className=" bg-white rounded-md shadow-md p-3">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Recent Payrolls</h3>
            {/* <button className="text-blue-600 text-sm font-medium hover:text-blue-800">
              View All →
            </button> */}
          </div>
          
          <div className="overflow-x-auto bg-white dark:bg-gray-900 shadow-md rounded-lg font-poppins">
            <table className="min-w-full table-auto md:table-fixed text-sm">
              <thead className="bg-gray-100 dark:bg-gray-800 dark:text-white">
                <tr>
                  <th className="whitespace-nowrap px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider">Period</th>
                  <th className="whitespace-nowrap px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider">Gross</th>
                  <th className="whitespace-nowrap px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider">Net</th>
                  <th className="whitespace-nowrap px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider">Status</th>
                  <th className="whitespace-nowrap px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {recentPayrolls.slice(0, 5).map((record) => (
                  <tr key={record.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {months[record.payroll_month - 1]} {record.payroll_year}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      ₹ {record.gross_salary?.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      ₹ {record.net_salary?.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {/* <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        record.status === 'paid' ? 'bg-green-100 text-green-800' :
                        record.status === 'approved' ? 'bg-blue-100 text-blue-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {record.status}
                      </span> */}
                      <StatusBadge status={record.status} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => downloadPayslip(record.id)}
                        className="text-blue-600 hover:text-blue-900 flex items-center"
                      >
                        {/* <Download className="h-4 w-4 mr-1" /> */}
                        Download
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Current Month Summary */}
        {/* {latestPayroll && (
          <div className=" bg-white shadow-md rounded-md p-3">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Current Month Summary</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-black">₹ {latestPayroll.gross_salary?.toLocaleString()}</p>
                <p className="text-sm text-black">Gross Salary</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-black">₹ {latestPayroll.total_deductions?.toLocaleString()}</p>
                <p className="text-sm text-black">Total Deductions</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-black">₹ {latestPayroll.net_salary?.toLocaleString()}</p>
                <p className="text-sm text-black">Net Salary</p>
              </div>
            </div>
          </div>
        )} */}
      </div>
    </div>
  );
};

export default EmployeeDashboard;